import { Rng, noise1 } from '../core/rng';
import { clamp } from '../core/math';
import {
  CELL,
  CellState,
  GRID_H,
  GRID_W,
  Grid,
  House,
  Road,
  RoadCell,
  T,
  TERRAIN,
  idx,
  inBounds,
} from './types';
import type { Difficulty } from './difficulty';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A named cluster of buildings. This is what you actually give an engine as an objective. */
export interface Village {
  id: number;
  name: string;
  /** Centre of the cluster, in cells. */
  cx: number;
  cy: number;
  houseIds: number[];
  roadId: number;
  /** Nearest point on that road. */
  accessS: number;
}

/**
 * Somewhere a fire engine can refill its water tank: an acequia (an old open
 * irrigation channel), an alberca (a walled irrigation tank) or the reservoir.
 */
export interface WaterPoint {
  x: number;
  y: number;
  roadId: number;
  s: number;
  kind: 'acequia' | 'alberca' | 'reservoir';
}

export interface Mountain {
  /** Ridge line: the y of the skyline for each column. */
  ridge: Int16Array;
  /** Altitude 0..1 for each cell, relative to the ridge in that column. */
  alt: Float32Array;
  roads: Road[];
  houses: House[];
  villages: Village[];
  waterPoints: WaterPoint[];
  helipad: Rect;
  base: Rect;
  /** Cells of the reservoir, for the scoop check and the water-level readout. */
  reservoir: number[];
  ignitions: number[];
}

const VILLAGES: readonly { name: string; alt: number; weight: number; road: 0 | 1; count: number }[] = [
  // Lanjarón sits well above the Ízbor valley floor, which also leaves the fire
  // some ground to cross before it reaches the first buildings.
  { name: 'Lanjarón', alt: 0.2, weight: 5.0, road: 1, count: 5 },
  { name: 'Barrio Alto', alt: 0.27, weight: 3.0, road: 0, count: 4 },
  { name: 'Cortijo de Tello', alt: 0.33, weight: 2.2, road: 1, count: 3 },
  { name: 'Acequia Nueva', alt: 0.41, weight: 2.0, road: 0, count: 3 },
  { name: 'El Chorreón', alt: 0.5, weight: 1.8, road: 1, count: 2 },
  { name: 'Cortijo Blanco', alt: 0.58, weight: 1.4, road: 0, count: 2 },
  { name: 'Cortijo del Cura', alt: 0.7, weight: 1.2, road: 1, count: 2 },
  { name: 'Refugio Ventura', alt: 0.79, weight: 0.8, road: 0, count: 1 },
];

/** Tight zigzag bends down the west side of the hill, out toward Béznar and the dam. */
const ROAD_WEST: readonly [number, number][] = [
  [62, 9],
  [52, 15],
  [61, 22],
  [44, 28],
  [54, 35],
  [34, 41],
  [44, 48],
  [22, 52],
  [30, 56],
  [8, 57],
  [0, 58],
];

/** Tight zigzag bends down the east side of the hill, out toward Órgiva. */
const ROAD_EAST: readonly [number, number][] = [
  [67, 9],
  [78, 16],
  [68, 23],
  [83, 30],
  [70, 37],
  [87, 44],
  [74, 50],
  [93, 55],
  [80, 60],
  [101, 64],
  [112, 67],
  [119, 68],
];

/** How many cells a start must be from the nearest building, so crews have time to arrive. */
const MIN_IGNITION_STANDOFF = 20;
/** Refill points every road is guaranteed, so no crew is ever left with an empty tank. */
const MIN_WATER_POINTS = 5;

const HELIPAD: Rect = { x: 60, y: 0, w: 9, h: 3 }; // y filled in from the ridge
const BASE: Rect = { x: 62, y: 70, w: 10, h: 4 };

export function generate(grid: Grid, rng: Rng, diff: Difficulty): Mountain {
  const ridge = new Int16Array(GRID_W);
  const alt = new Float32Array(GRID_W * GRID_H);

  // --- Skyline -------------------------------------------------------------
  const ridgeNoise = noise1(rng.fork(), GRID_W, 4);
  const summitX = 64;
  for (let x = 0; x < GRID_W; x++) {
    const d = (x - summitX) / 36;
    const shoulder = 1 - Math.exp(-(d * d) / 2);
    ridge[x] = Math.round(clamp(5 + 13 * shoulder + ridgeNoise[x] * 1.4, 3, 21));
  }

  // --- Vegetation bands by altitude ---------------------------------------
  const bandNoise = noise1(rng.fork(), GRID_W, 5);
  for (let x = 0; x < GRID_W; x++) {
    const span = GRID_H - 1 - ridge[x];
    for (let y = 0; y < GRID_H; y++) {
      const i = idx(x, y);
      grid.tex[i] = rng.int(0, 255);
      if (y < ridge[x]) {
        grid.terrain[i] = T.Sky;
        alt[i] = 1.2;
        continue;
      }
      const a = clamp((GRID_H - 1 - y) / Math.max(1, span), 0, 1);
      alt[i] = a;
      const jitter = bandNoise[x] * 0.05 + (grid.tex[i] / 255 - 0.5) * 0.03;
      const av = a + jitter;
      let t: T;
      if (av > 0.86) t = T.Rock;
      else if (av > 0.6) t = grid.tex[i] > 232 && av > 0.78 ? T.Rock : T.Pine;
      else if (av > 0.32) t = grid.tex[i] > 214 ? T.Pine : T.Scrub;
      else if (av > 0.13) t = grid.tex[i] > 236 ? T.Scrub : T.Olive;
      else t = T.Grass;
      grid.terrain[i] = t;
    }
  }

  // --- Embalse de Béznar, the reservoir, bottom left -----------------------
  const reservoir: number[] = [];
  // Big enough that the pilot can hover over it without drifting onto land.
  const rc = { x: 25, y: 68, rx: 21, ry: 5.6 };
  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      const dx = (x - rc.x) / rc.rx;
      const dy = (y - rc.y) / rc.ry;
      if (dx * dx + dy * dy <= 1) {
        const i = idx(x, y);
        grid.terrain[i] = T.Water;
        reservoir.push(i);
      }
    }
  }

  // --- Acequias: the old irrigation channels are also breaks in the fuel ---
  const acequiaNoise = noise1(rng.fork(), GRID_W, 3);
  for (const targetAlt of [0.3, 0.48, 0.62]) {
    for (let x = 10; x < GRID_W - 10; x++) {
      // Leave gaps where the channel runs through a buried pipe or has fallen
      // in. Big gaps also stop them looking like three unbroken lines drawn
      // across the map.
      if (acequiaNoise[x] > 0.35) continue;
      const span = GRID_H - 1 - ridge[x];
      const y = Math.round(GRID_H - 1 - (targetAlt + acequiaNoise[x] * 0.02) * span);
      if (!inBounds(x, y)) continue;
      const i = idx(x, y);
      if (grid.terrain[i] !== T.Sky && grid.terrain[i] !== T.Water) grid.terrain[i] = T.Acequia;
    }
  }

  // --- Roads ---------------------------------------------------------------
  const roads: Road[] = [
    { id: 0, name: 'A-348 west · Béznar', cells: traceRoad(ROAD_WEST), cut: null, blocked: new Float32Array(0), blockedBehind: new Float32Array(0), riskAhead: new Float32Array(0), scorched: new Uint8Array(0), jam: 0, through: 0 },
    { id: 1, name: 'A-348 east · Órgiva', cells: traceRoad(ROAD_EAST), cut: null, blocked: new Float32Array(0), blockedBehind: new Float32Array(0), riskAhead: new Float32Array(0), scorched: new Uint8Array(0), jam: 0, through: 0 },
  ];
  for (const road of roads) {
    for (const c of road.cells) grid.terrain[idx(c.x, c.y)] = T.Road;
  }

  // --- Pads ----------------------------------------------------------------
  const helipad: Rect = { ...HELIPAD, y: ridge[HELIPAD.x + 4] + 2 };
  // Put bare rock around the assembly point so that nothing can burn up there.
  fill(grid, { x: helipad.x - 3, y: helipad.y - 2, w: helipad.w + 6, h: helipad.h + 4 }, T.Rock, true);
  fill(grid, helipad, T.Helipad, true);
  const base: Rect = { ...BASE };
  fill(grid, { x: base.x - 2, y: base.y - 1, w: base.w + 4, h: base.h + 2 }, T.Grass, true);
  fill(grid, base, T.Base, true);

  // --- Villages ------------------------------------------------------------
  const { houses, villages } = placeHouses(grid, roads, alt, diff);

  // --- Albercas: the walled irrigation tanks beside the villages -----------
  // These are what make protecting a village possible. The acequias only run
  // across the middle of the mountain, so without albercas the high cortijos
  // (the old hill farmhouses) would have no water an engine could reach at all.
  for (const v of villages) {
    if (v.cy < ridge[clamp(v.cx, 0, GRID_W - 1)] + 3) continue;
    const side = v.cx < GRID_W / 2 ? -1 : 1;
    const ax = clamp(v.cx + side * 5, 1, GRID_W - 3);
    const ay = clamp(v.cy + 2, 1, GRID_H - 3);
    for (let y = ay; y < ay + 2; y++)
      for (let x = ax; x < ax + 2; x++) {
        const i = idx(x, y);
        if (grid.terrain[i] === T.Sky || grid.terrain[i] === T.Road || grid.terrain[i] === T.Building) continue;
        grid.terrain[i] = T.Acequia;
      }
  }

  // --- Water points an engine can actually reach from a road ---------------
  // Every road must have several, whatever the acequia noise did: an engine
  // with nowhere to refill is simply out of the game once its tank is empty.
  const waterPoints = findWaterPoints(grid, roads);
  for (const road of roads) {
    const have = waterPoints.filter((p) => p.roadId === road.id).length;
    for (let k = have; k < MIN_WATER_POINTS; k++) {
      const s = Math.round(((k + 0.5) / MIN_WATER_POINTS) * (road.cells.length - 1));
      const c = road.cells[clamp(s, 0, road.cells.length - 1)];
      const side = c.x < GRID_W / 2 ? 1 : -1;
      const ax = clamp(c.x + side * 2, 1, GRID_W - 3);
      const ay = clamp(c.y - 2, 1, GRID_H - 3);
      let placed = false;
      for (let y = ay; y < ay + 2; y++)
        for (let x = ax; x < ax + 2; x++) {
          const i = idx(x, y);
          if (grid.terrain[i] === T.Sky || grid.terrain[i] === T.Road || grid.terrain[i] === T.Building) continue;
          grid.terrain[i] = T.Acequia;
          placed = true;
        }
      if (placed) waterPoints.push({ x: ax, y: ay, roadId: road.id, s, kind: 'alberca' });
    }
  }

  // --- Initial state -------------------------------------------------------
  for (let i = 0; i < grid.terrain.length; i++) {
    const def = TERRAIN[grid.terrain[i]];
    grid.fuel[i] = def.fuel;
    // Moisture only means anything where there is fuel to be damp. Leaving rock
    // and tarmac at zero is what keeps "wet" worth reading everywhere else.
    grid.moist[i] = def.fuel > 0 ? clamp(def.moist * (1 - diff.ambientHeat * 0.6), 0.02, 1) : 0;
    grid.state[i] = CellState.Unburnt;
    grid.heat[i] = 0;
    grid.inten[i] = 0;
  }

  // --- Ignitions: low in the barranco, the steep gully below the villages --
  // A start has to be in fuel that will actually keep the fire going, or the
  // incident is over before it begins. We score the whole patch around the
  // cell, not just the one cell.
  const ignitions: number[] = [];
  for (let n = 0; n < diff.ignitions; n++) {
    const wantX = Math.round(((n + 1) / (diff.ignitions + 1)) * (GRID_W - 24) + 12);
    let best = -1;
    let bestScore = -Infinity;
    for (let tries = 0; tries < 600; tries++) {
      const x = clamp(Math.round(wantX + rng.range(-14, 14)), 3, GRID_W - 4);
      const span = GRID_H - 1 - ridge[x];
      // Down in the gully, below everything else.
      const y = Math.round(GRID_H - 1 - rng.range(0.05, 0.14) * span);
      if (!inBounds(x, y)) continue;
      const i = idx(x, y);
      if (TERRAIN[grid.terrain[i]].fuel <= 0) continue;

      // And well clear of the nearest buildings. Height alone is not enough
      // separation: fire moves fast through valley grass, so a start close to a
      // village reaches it before anybody could possibly be sent out.
      let nearest = Infinity;
      for (const h of houses) {
        nearest = Math.min(nearest, Math.hypot(h.x + h.w / 2 - x, h.y + h.h / 2 - y));
      }
      if (nearest < MIN_IGNITION_STANDOFF) continue;

      let carry = 0;
      for (let dy = -2; dy <= 2; dy++)
        for (let dx = -2; dx <= 2; dx++) {
          if (!inBounds(x + dx, y + dy)) continue;
          const def = TERRAIN[grid.terrain[idx(x + dx, y + dy)]];
          if (def.fuel > 0) carry += def.fuel / Math.max(0.1, def.ign);
        }
      const score = carry - Math.abs(x - wantX) * 0.35 + Math.min(nearest, 40) * 0.4;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best >= 0) ignitions.push(best);
  }

  return { ridge, alt, roads, houses, villages, waterPoints, helipad, base, reservoir, ignitions };
}

/**
 * An engine refills from a hydrant, an acequia or an alberca it can park beside.
 * Anything more than a couple of cells from the road is no use to its pump.
 */
function findWaterPoints(grid: Grid, roads: Road[]): WaterPoint[] {
  const points: WaterPoint[] = [];
  const claimed = new Set<number>();
  for (const road of roads) {
    road.cells.forEach((c, s) => {
      // Only look every few cells, or every bend of the road becomes a hydrant.
      if (s % 4 !== 0) return;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const x = c.x + dx;
          const y = c.y + dy;
          if (!inBounds(x, y)) continue;
          const i = idx(x, y);
          const t = grid.terrain[i];
          if (t !== T.Acequia && t !== T.Water) continue;
          if (claimed.has(i)) continue;
          // Space them out so the list does not fill up with near-duplicates.
          if (points.some((p) => Math.abs(p.s - s) < 8 && p.roadId === road.id)) continue;
          claimed.add(i);
          points.push({ x, y, roadId: road.id, s, kind: t === T.Water ? 'reservoir' : 'acequia' });
          return;
        }
      }
    });
  }
  return points;
}

/** Turn a list of corner points into an ordered list of road cells with speed limits. */
function traceRoad(points: readonly [number, number][]): RoadCell[] {
  const cells: RoadCell[] = [];
  const hairpinAt: number[] = [];
  const push = (x: number, y: number) => {
    const cx = clamp(Math.round(x), 0, GRID_W - 1);
    const cy = clamp(Math.round(y), 0, GRID_H - 1);
    const last = cells[cells.length - 1];
    if (last && last.x === cx && last.y === cy) return;
    cells.push({ x: cx, y: cy, limit: 4.0 });
  };
  for (let p = 0; p < points.length - 1; p++) {
    const [x0, y0] = points[p];
    const [x1, y1] = points[p + 1];
    hairpinAt.push(cells.length);
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.max(1, Math.ceil(dist * 2));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      push(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    }
  }
  // Hairpins, the tightest bends, are where the queues build: cap the speed there.
  for (const h of hairpinAt) {
    for (let d = -3; d <= 3; d++) {
      const c = cells[h + d];
      if (c) c.limit = Math.min(c.limit, 1.1);
    }
  }
  // The exit run is open road.
  for (let i = cells.length - 4; i < cells.length; i++) if (cells[i]) cells[i].limit = 5.0;
  return cells;
}

function fill(grid: Grid, r: Rect, t: T, skySafe: boolean): void {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (!inBounds(x, y)) continue;
      const i = idx(x, y);
      if (skySafe && grid.terrain[i] === T.Sky) continue;
      grid.terrain[i] = t;
    }
  }
}

function placeHouses(
  grid: Grid,
  roads: Road[],
  alt: Float32Array,
  diff: Difficulty,
): { houses: House[]; villages: Village[] } {
  const houses: House[] = [];
  const villages: Village[] = [];
  const totalWeight = VILLAGES.reduce((a, v) => a + v.weight, 0);
  let assigned = 0;
  let id = 0;

  VILLAGES.forEach((village, vi) => {
    const road = roads[village.road];
    // Find the point on this road closest to the village's altitude.
    let bestS = 0;
    let bestErr = Infinity;
    road.cells.forEach((c, s) => {
      const err = Math.abs(alt[idx(c.x, c.y)] - village.alt);
      if (err < bestErr) {
        bestErr = err;
        bestS = s;
      }
    });
    const access = road.cells[bestS];

    const cluster: number[] = [];
    const isLast = vi === VILLAGES.length - 1;
    const share = isLast
      ? diff.residents - assigned
      : Math.max(village.count, Math.round((village.weight / totalWeight) * diff.residents));
    const residents = Math.max(village.count, share);
    assigned += residents;

    // Buildings sit off the road on whichever side has room, in a tight cluster
    // rather than a line, because a village has to fit inside the length of hose
    // a crew can run out from the road.
    const side = access.x < GRID_W / 2 ? 1 : -1;
    for (let b = 0; b < village.count; b++) {
      const ox = access.x + side * (3 + (b % 2) * 4);
      const oy = access.y + (Math.floor(b / 2) * 4 - 3);
      const r: Rect = { x: clamp(ox, 1, GRID_W - 4), y: clamp(oy, 1, GRID_H - 4), w: 3, h: 3 };
      let habitable = false;
      for (let y = r.y; y < r.y + r.h; y++)
        for (let x = r.x; x < r.x + r.w; x++) {
          const i = idx(x, y);
          if (grid.terrain[i] === T.Sky || grid.terrain[i] === T.Water || grid.terrain[i] === T.Road) continue;
          grid.terrain[i] = T.Building;
          habitable = true;
        }
      if (!habitable) continue;
      const perHouse = Math.floor(residents / village.count) + (b < residents % village.count ? 1 : 0);
      cluster.push(id);
      houses.push({
        id: id++,
        villageId: villages.length,
        name: village.count > 1 ? `${village.name} ${b + 1}` : village.name,
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
        residents: perHouse,
        roadId: road.id,
        accessS: bestS,
        accessX: access.x,
        accessY: access.y,
      });
    }

    if (cluster.length > 0) {
      const members = cluster.map((hid) => houses.find((h) => h.id === hid)!);
      villages.push({
        id: villages.length,
        name: village.name,
        cx: Math.round(members.reduce((a, h) => a + h.x + h.w / 2, 0) / members.length),
        cy: Math.round(members.reduce((a, h) => a + h.y + h.h / 2, 0) / members.length),
        houseIds: cluster,
        roadId: road.id,
        accessS: bestS,
      });
    }
  });

  // Trim or top up so the resident count matches the difficulty exactly.
  let sum = houses.reduce((a, h) => a + h.residents, 0);
  let guard = 0;
  while (sum !== diff.residents && guard++ < 1000) {
    const h = houses[guard % houses.length];
    if (sum > diff.residents && h.residents > 1) {
      h.residents--;
      sum--;
    } else if (sum < diff.residents) {
      h.residents++;
      sum++;
    }
  }
  return { houses, villages };
}

/** Cell coordinates to logical pixel centre. */
export const cellToPx = (c: number): number => c * CELL + CELL / 2;
